import React, { useEffect, useMemo, useState } from "react";
import {
  FaBalanceScale,
  FaBuilding,
  FaEnvelope,
  FaExternalLinkAlt,
  FaFileContract,
  FaGavel,
  FaGlobe,
  FaIdCard,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaShieldAlt,
  FaUserTie,
} from "react-icons/fa";

import useScrollToTop from "../../hooks/useScrollToTop";
import { getSettings } from "../../services/settingsServices";
import Footer from "../Home/HomePage/Footer";
import ScrollIndicator from "../../components/ScrollIndicator/ScrollIndicator";

const DEFAULT_SETTINGS = {
  siteweb_club: "",
  nom_club: "",
  adresse_club: "",
  responsable_club: "",
  mail_club: "",
  siret_club: "",
  tel_club: "",
  hebergeur_nom: "",
  hebergeur_url: "",
};

const getDisplayValue = (value, fallback = "Non renseigné") => {
  const clean = String(value || "").trim();
  return clean || fallback;
};

const isExternalURL = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const normalizeWebsite = (site = "") => {
  const clean = String(site || "").trim();

  if (!clean && typeof window !== "undefined") {
    return window.location.origin;
  }

  return clean || "";
};

const SectionCard = ({ id, icon: Icon, title, children }) => (
  <article
    id={id}
    className="scroll-mt-32 rounded-[2rem] border border-slate-200 bg-white/90 p-6 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/85 sm:p-8"
  >
    <div className="mb-5 flex items-start gap-4">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <Icon />
      </div>

      <div>
        <h2 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white sm:text-3xl">
          {title}
        </h2>
      </div>
    </div>

    <div className="space-y-4 text-sm leading-7 text-slate-600 dark:text-slate-300 sm:text-base">
      {children}
    </div>
  </article>
);

const InfoRow = ({ icon: Icon, label, value, href }) => {
  if (!value) return null;

  const content = (
    <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
      <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-primary shadow-sm dark:bg-slate-950">
        <Icon />
      </div>

      <div className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
          {label}
        </p>
        <p className="mt-1 break-words text-sm font-semibold leading-6 text-slate-700 dark:text-slate-200">
          {value}
        </p>
      </div>
    </div>
  );

  if (!href) return content;

  return (
    <a href={href} className="block transition hover:-translate-y-0.5">
      {content}
    </a>
  );
};

const SummaryCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-[1.7rem] border border-slate-200 bg-white/85 p-5 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
    <Icon className="mb-4 text-2xl text-primary" />
    <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
      {label}
    </p>
    <p className="mt-2 break-words font-barlow text-xl font-bold text-slate-950 dark:text-white">
      {value}
    </p>
  </div>
);

export default function MentionsLegalesPage() {
  useScrollToTop();

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        const clubSettings = Array.isArray(data) ? data[0] : data;

        if (!mounted || !clubSettings) return;

        setSettings({
          ...DEFAULT_SETTINGS,
          ...clubSettings,
          siteweb_club:
            clubSettings.siteweb_club ||
            clubSettings.website ||
            clubSettings.site_url ||
            "",
          mail_club:
            clubSettings.mail_club ||
            clubSettings.email_club ||
            clubSettings.email ||
            "",
          tel_club:
            clubSettings.tel_club ||
            clubSettings.telephone_club ||
            clubSettings.phone ||
            "",
          adresse_club:
            clubSettings.adresse_club ||
            clubSettings.address ||
            [clubSettings.adresse, clubSettings.code_postal, clubSettings.ville]
              .filter(Boolean)
              .join(" "),
          responsable_club:
            clubSettings.responsable_club ||
            clubSettings.responsable_publication ||
            clubSettings.president_club ||
            "",
        });
      } catch (error) {
        console.error("Erreur lors du chargement des mentions légales :", error);
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const siteUrl = useMemo(
    () => normalizeWebsite(settings.siteweb_club),
    [settings.siteweb_club]
  );

  const mailHref = useMemo(() => {
    return settings.mail_club ? `mailto:${settings.mail_club}` : "";
  }, [settings.mail_club]);

  const hostName =
    settings.hebergeur_nom ||
    settings.hosting_name ||
    "Hébergeur technique du site";

  const hostUrl =
    settings.hebergeur_url ||
    settings.hosting_url ||
    "Informations disponibles auprès de l’éditeur du site";

  const currentDate = new Date().toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });

  const navItems = [
    ["Informations légales", "informations-legales"],
    ["Utilisation du site", "utilisation-site"],
    ["Services fournis", "services-fournis"],
    ["Propriété intellectuelle", "propriete-intellectuelle"],
    ["Responsabilité", "responsabilite"],
    ["Données personnelles", "donnees-personnelles"],
    ["Liens et cookies", "liens-cookies"],
  ];

  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <main>
        <section className="relative overflow-hidden bg-slate-950 px-4 pb-16 pt-36 text-white sm:px-6 lg:px-8 lg:pb-20 lg:pt-40">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.30),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.22),transparent_32%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:44px_44px] opacity-20" />

          <div className="relative mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-white/70 backdrop-blur-xl">
                <FaBalanceScale className="text-primary" />
                Textes réglementaires
              </div>

              <h1 className="mt-6 font-barlow text-5xl font-bold leading-tight tracking-tight text-white sm:text-6xl lg:text-7xl">
                Mentions
                <br />
                légales
              </h1>

              <p className="mt-6 max-w-2xl text-base leading-8 text-white/75 sm:text-lg">
                Cette page présente les informations relatives à l’éditeur du
                site, à son hébergement, aux conditions d’utilisation et aux
                principales règles applicables aux contenus et données
                personnelles.
              </p>
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden bg-slate-50 py-20 dark:bg-slate-950 lg:py-28">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.12),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.13)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.13)_1px,transparent_1px)] bg-[size:42px_42px] opacity-35 dark:opacity-15" />

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-5 md:grid-cols-3">
              <SummaryCard
                icon={FaBuilding}
                label="Éditeur"
                value={getDisplayValue(settings.nom_club, "Club / association")}
              />

              <SummaryCard
                icon={FaGlobe}
                label="Site internet"
                value={getDisplayValue(siteUrl)}
              />

              <SummaryCard
                icon={FaEnvelope}
                label="Contact"
                value={getDisplayValue(settings.mail_club)}
              />
            </div>

            <div className="mt-10 grid gap-8 lg:grid-cols-[280px_1fr]">
              <aside className="hidden lg:block">
                <div className="sticky top-32 rounded-[2rem] border border-slate-200 bg-white/90 p-5 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/85">
                  <p className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                    Sommaire
                  </p>

                  <nav className="flex flex-col gap-2">
                    {navItems.map(([label, id]) => (
                      <a
                        key={id}
                        href={`#${id}`}
                        className="rounded-2xl px-4 py-3 text-sm font-bold text-slate-600 transition hover:bg-orange-50 hover:text-primary dark:text-slate-300 dark:hover:bg-white/10"
                      >
                        {label}
                      </a>
                    ))}
                  </nav>

                  <div className="mt-5 rounded-2xl bg-orange-50 p-4 text-sm leading-6 text-orange-800 dark:bg-orange-500/10 dark:text-orange-300">
                    <div className="mb-2 flex items-center gap-2 font-bold">
                      <FaInfoCircle />
                      Mise à jour
                    </div>
                    Dernière mise à jour automatique : {currentDate}.
                  </div>
                </div>
              </aside>

              <div className="space-y-8">
                <SectionCard
                  id="informations-legales"
                  icon={FaIdCard}
                  title="1. Informations légales"
                >
                  <p>
                    Conformément aux dispositions applicables aux services de
                    communication au public en ligne, il est précisé aux
                    utilisateurs du site l’identité des différents intervenants
                    dans le cadre de sa réalisation, de son édition et de son
                    suivi.
                  </p>

                  <div className="grid gap-4 md:grid-cols-2">
                    <InfoRow
                      icon={FaBuilding}
                      label="Éditeur du site"
                      value={getDisplayValue(settings.nom_club, "Club / association")}
                    />

                    <InfoRow
                      icon={FaMapMarkerAlt}
                      label="Adresse"
                      value={getDisplayValue(settings.adresse_club)}
                    />

                    <InfoRow
                      icon={FaUserTie}
                      label="Responsable de publication"
                      value={getDisplayValue(settings.responsable_club)}
                    />

                    <InfoRow
                      icon={FaEnvelope}
                      label="Email de contact"
                      value={getDisplayValue(settings.mail_club)}
                      href={mailHref}
                    />

                    <InfoRow
                      icon={FaFileContract}
                      label="Numéro SIRET / RNA"
                      value={getDisplayValue(settings.siret_club, "Non renseigné ou non applicable")}
                    />

                    <InfoRow
                      icon={FaGlobe}
                      label="Site internet"
                      value={getDisplayValue(siteUrl)}
                      href={isExternalURL(siteUrl) ? siteUrl : undefined}
                    />

                    <InfoRow
                      icon={FaShieldAlt}
                      label="Hébergeur"
                      value={hostName}
                    />

                    <InfoRow
                      icon={FaExternalLinkAlt}
                      label="Site de l’hébergeur"
                      value={hostUrl}
                      href={isExternalURL(hostUrl) ? hostUrl : undefined}
                    />
                  </div>

                  <p className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm dark:border-white/10 dark:bg-white/5">
                    Les informations non renseignées doivent être complétées par
                    l’éditeur du site depuis l’espace d’administration lorsque
                    ces informations sont nécessaires à l’identification de la
                    structure.
                  </p>
                </SectionCard>

                <SectionCard
                  id="utilisation-site"
                  icon={FaGavel}
                  title="2. Conditions générales d’utilisation du site"
                >
                  <p>
                    L’utilisation du site implique l’acceptation pleine et
                    entière des présentes mentions légales et conditions
                    d’utilisation. Celles-ci peuvent être modifiées ou
                    complétées à tout moment afin de tenir compte de l’évolution
                    du site, de ses services ou de la réglementation applicable.
                  </p>

                  <p>
                    L’utilisateur est invité à consulter régulièrement cette
                    page afin de prendre connaissance des éventuelles mises à
                    jour.
                  </p>
                </SectionCard>

                <SectionCard
                  id="services-fournis"
                  icon={FaInfoCircle}
                  title="3. Description des services fournis"
                >
                  <p>
                    Le site a pour objet de fournir des informations relatives à
                    la vie du club ou de l’association : présentation,
                    activités, disciplines, horaires, tarifs, actualités,
                    albums photos, partenaires, contacts et, le cas échéant,
                    formulaires de contact ou de demande d’inscription.
                  </p>

                  <p>
                    L’éditeur du site s’efforce de fournir des informations
                    aussi précises et à jour que possible. Toutefois, des erreurs,
                    omissions ou retards de mise à jour peuvent survenir. Les
                    informations diffusées sur le site sont données à titre
                    indicatif et peuvent être modifiées sans préavis.
                  </p>
                </SectionCard>

                <SectionCard
                  id="propriete-intellectuelle"
                  icon={FaFileContract}
                  title="4. Propriété intellectuelle"
                >
                  <p>
                    Les contenus publiés sur le site, notamment les textes,
                    images, photographies, graphismes, logos, icônes, documents
                    et éléments de mise en page, sont protégés par le droit de la
                    propriété intellectuelle.
                  </p>

                  <p>
                    Sauf mention contraire ou autorisation préalable, toute
                    reproduction, représentation, modification, publication,
                    adaptation ou exploitation de tout ou partie des éléments du
                    site est interdite, quel que soit le moyen ou le procédé
                    utilisé.
                  </p>

                  <p>
                    Les marques, logos ou contenus appartenant à des tiers
                    restent la propriété de leurs titulaires respectifs.
                  </p>
                </SectionCard>

                <SectionCard
                  id="responsabilite"
                  icon={FaShieldAlt}
                  title="5. Limitations de responsabilité"
                >
                  <p>
                    L’éditeur du site ne pourra être tenu responsable des
                    dommages directs ou indirects causés au matériel de
                    l’utilisateur lors de l’accès au site, notamment en cas de
                    bug, incompatibilité, interruption, maintenance, perte de
                    données ou indisponibilité temporaire du service.
                  </p>

                  <p>
                    L’utilisateur s’engage à accéder au site avec un matériel
                    récent, ne contenant pas de virus, et avec un navigateur mis
                    à jour. L’éditeur ne pourra être tenu responsable de
                    l’utilisation faite par l’utilisateur des informations
                    disponibles sur le site.
                  </p>
                </SectionCard>

                <SectionCard
                  id="donnees-personnelles"
                  icon={FaShieldAlt}
                  title="6. Données personnelles"
                >
                  <p>
                    Le site peut collecter des données personnelles lorsque
                    l’utilisateur utilise certains services, notamment un
                    formulaire de contact, un formulaire de demande
                    d’inscription, ou lorsqu’il échange avec le club ou
                    l’association.
                  </p>

                  <p>
                    Les données collectées peuvent notamment comprendre :
                    identité, coordonnées, message transmis, informations utiles
                    à une demande d’inscription, ainsi que les informations
                    techniques strictement nécessaires au bon fonctionnement et à
                    la sécurité du site.
                  </p>

                  <p>
                    Ces données sont utilisées uniquement pour répondre aux
                    demandes des utilisateurs, gérer les inscriptions, assurer le
                    suivi administratif du club ou de l’association, et
                    améliorer le fonctionnement du site.
                  </p>

                  <p>
                    Conformément à la réglementation applicable en matière de
                    protection des données, l’utilisateur peut demander l’accès,
                    la rectification, l’effacement ou l’opposition au traitement
                    de ses données personnelles en contactant l’éditeur du site à
                    l’adresse suivante :{" "}
                    {settings.mail_club ? (
                      <a
                        href={mailHref}
                        className="font-bold text-primary hover:underline"
                      >
                        {settings.mail_club}
                      </a>
                    ) : (
                      <strong>email de contact non renseigné</strong>
                    )}
                    .
                  </p>

                  <p className="rounded-2xl border border-orange-200 bg-orange-50 p-4 text-sm text-orange-800 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                    Pour plus de détails sur les traitements de données,
                    l’utilisateur est invité à consulter la politique de
                    confidentialité du site lorsqu’elle est disponible.
                  </p>
                </SectionCard>

                <SectionCard
                  id="liens-cookies"
                  icon={FaGlobe}
                  title="7. Liens hypertextes et cookies"
                >
                  <p>
                    Le site peut contenir des liens hypertextes vers d’autres
                    sites internet ou services tiers, notamment des réseaux
                    sociaux, partenaires, fédérations, organismes publics ou
                    plateformes externes. L’éditeur du site ne peut pas contrôler
                    en permanence le contenu de ces sites tiers et décline toute
                    responsabilité quant à leur contenu ou leur fonctionnement.
                  </p>

                  <p>
                    La navigation sur le site est susceptible d’entraîner
                    l’utilisation de cookies ou traceurs strictement nécessaires
                    au bon fonctionnement du site, à la mesure d’audience ou à
                    l’amélioration de l’expérience utilisateur lorsque ces
                    fonctionnalités sont activées.
                  </p>

                  <p>
                    L’utilisateur peut configurer son navigateur pour refuser ou
                    supprimer les cookies. Le refus de certains cookies peut
                    toutefois limiter l’accès à certaines fonctionnalités du
                    site.
                  </p>
                </SectionCard>
              </div>
            </div>
          </div>
        </section>

        <Footer />
        <ScrollIndicator />
      </main>
    </div>
  );
}